import { Injectable, signal } from '@angular/core';
import {
  TutorialModuleConfig,
  TutorialModuleProgress,
  TutorialProfile,
  TutorialStats,
} from './tutorial.models';

function emptyProfile(): TutorialProfile {
  return {
    modules: {},
    stats: { totalCompleted: 0, totalStarted: 0, avgRating: 0, xp: 0, level: 1 },
  };
}

function emptyProgress(tutorialId: string, totalSteps: number): TutorialModuleProgress {
  return {
    tutorialId,
    startedAt: null,
    completedAt: null,
    rating: null,
    stepsVisited: [],
    totalSteps,
    timeSpentSec: 0,
    currentSectionId: null,
    sectionsCompleted: [],
    dismissed: false,
    sectionsDismissed: [],
    status: 'not_started',
  };
}

@Injectable({ providedIn: 'root' })
export class TutorialStoreService {

  // --- Session state (active tutorial navigation) ---
  readonly activeModuleId = signal<string | null>(null);
  readonly activeConfig = signal<TutorialModuleConfig | null>(null);
  readonly activeSectionId = signal<string | null>(null);
  readonly currentStepIndex = signal<number>(0);
  readonly isActive = signal<boolean>(false);

  /** When true, HTTP requests are intercepted: GETs return mock data, writes are simulated */
  readonly isDemoMode = signal<boolean>(
    (() => { try { return JSON.parse(localStorage.getItem('tutorial-demo-mode') ?? 'false'); } catch { return false; } })()
  );
  /** Commands for the sidenav to execute (open drawer, expand section) */
  readonly sidenavCommand = signal<{ type: 'open-drawer' | 'close-drawer' | 'expand-section'; section?: string } | null>(null);

  /** Whether the sidenav drawer is currently open (set by home component) */
  readonly sidenavOpen = signal<boolean>(false);

  /**
   * Demo progress high-water mark — tracks the furthest point reached in tutorials.
   * Format: moduleOrder + globalStepIndex / 100 (e.g., 6.11 = module 6, step 11).
   * Sandbox entities with `availableAt <= demoProgress` are visible.
   */
  readonly demoProgress = signal<number>(
    (() => { try { return JSON.parse(localStorage.getItem('tutorial-demo-progress') ?? '0'); } catch { return 0; } })()
  );

  /** Incremented to signal the interceptor to clear its session entities */
  readonly clearSessionVersion = signal<number>(0);

  // --- Persistent state (loaded from backend) ---
  readonly profile = signal<TutorialProfile>(emptyProfile());
  readonly loaded = signal<boolean>(false);

  // --- Session state mutations ---

  setActiveModule(config: TutorialModuleConfig | null): void {
    this.activeConfig.set(config);
    this.activeModuleId.set(config?.moduleId ?? null);
    this.isActive.set(config != null);
    if (config && config.sections.length > 0) {
      this.activeSectionId.set(config.sections[0].id);
      this.currentStepIndex.set(0);
    } else {
      this.activeSectionId.set(null);
      this.currentStepIndex.set(0);
    }
  }

  setActiveSection(sectionId: string | null): void {
    this.activeSectionId.set(sectionId);
    this.currentStepIndex.set(0);
  }

  setStepIndex(index: number): void {
    this.currentStepIndex.set(index);
  }

  setDemoMode(value: boolean): void {
    this.isDemoMode.set(value);
    localStorage.setItem('tutorial-demo-mode', JSON.stringify(value));
  }

  resetSession(): void {
    this.activeConfig.set(null);
    this.activeModuleId.set(null);
    this.activeSectionId.set(null);
    this.currentStepIndex.set(0);
    this.isActive.set(false);
  }

  // --- Progress mutations ---

  /** Set the demo progress to the current tutorial position */
  setDemoProgress(value: number): void {
    this.demoProgress.set(value);
    localStorage.setItem('tutorial-demo-progress', JSON.stringify(value));
  }

  clearProgress(): void {
    this.demoProgress.set(0);
    localStorage.removeItem('tutorial-demo-progress');
  }

  // --- Profile mutations ---

  getOrCreateProgress(tutorialId: string, totalSteps: number): TutorialModuleProgress {
    const profile = this.profile();
    return profile.modules[tutorialId] ?? emptyProgress(tutorialId, totalSteps);
  }

  updateModuleProgress(tutorialId: string, data: Partial<TutorialModuleProgress>): void {
    const profile = this.profile();
    const existing = profile.modules[tutorialId] ?? emptyProgress(tutorialId, 0);
    const updated: TutorialProfile = {
      ...profile,
      modules: {
        ...profile.modules,
        [tutorialId]: { ...existing, ...data },
      },
    };
    this.profile.set(updated);
  }

  addDismissed(tutorialId: string): void {
    this.updateModuleProgress(tutorialId, { dismissed: true, status: 'dismissed' });
  }

  /** Load progression from backend response */
  loadFromBackend(serverProgress: TutorialModuleProgress[]): void {
    const modules: Record<string, TutorialModuleProgress> = {};
    for (const sp of serverProgress) {
      modules[sp.tutorialId] = sp;
    }
    const profile: TutorialProfile = { modules, stats: emptyProfile().stats };
    this.recalcStats(profile);
    this.profile.set(profile);
    this.loaded.set(true);
  }

  /** Reset all progression (debug) */
  resetProfile(): void {
    this.profile.set(emptyProfile());
    this.loaded.set(true);
  }

  recalcStats(profile?: TutorialProfile): void {
    const p = profile ?? this.profile();
    const modules = Object.values(p.modules);
    const completed = modules.filter((m) => m.status === 'completed');
    const started = modules.filter((m) => m.status === 'in_progress' || m.status === 'completed');
    const ratings = completed.filter((m) => m.rating != null).map((m) => m.rating!);
    const avgRating = ratings.length > 0 ? ratings.reduce((s, r) => s + r, 0) / ratings.length : 0;

    const xp = completed.reduce((sum, m) => {
      const base = 50;
      const ratingBonus = (m.rating ?? 0) * 10;
      const sectionBonus = m.sectionsCompleted.length * 5;
      return sum + base + ratingBonus + sectionBonus;
    }, 0);

    const stats: TutorialStats = {
      totalCompleted: completed.length,
      totalStarted: started.length,
      avgRating: Math.round(avgRating * 10) / 10,
      xp,
      level: Math.floor(xp / 100) + 1,
    };

    if (profile) {
      profile.stats = stats;
    } else {
      this.profile.set({ ...p, stats });
    }
  }
}
